import React, { useState, useEffect, useRef } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import breakpoints from 'utils/breakpoints';
import styled from 'styled-components';
import { IconButton } from 'components/components/atoms';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import HeartIcon from 'components/icons/Heart';
import ShareIcon from 'components/icons/Share';
import MailIcon from 'components/icons/Mail';
import ChatIcon from 'components/icons/ChatBubble';
import { getMobileOS } from 'utils/getMobileOS';
import { shareThis, getUrl } from 'utils/shareThis';
import getEmbedUrl from 'utils/getEmbedUrl';
import HeartFilled from 'components/icons/HeartFilled';
import { useSitecoreContext } from 'stores/sitecoreContext';
import getVideoType from 'utils/getVideoType';
import { track } from 'utils/tracking';
import trackingEvents from 'utils/trackingEvents';
import trackAnalyticsEvent from 'utils/trackAnalyticsEvent';
import { AspectRatio } from 'react-aspect-ratio';
import Slider from 'react-slick';
import { useSpring, animated } from '@react-spring/web';
import ChevronDown from 'components/icons/ChevronDown';
import { getScrFromIframe } from 'utils/getScrFromIframe';
import VideoIconGallery from 'components/icons/VideoIconGallery';
import TourIconGallery from 'components/icons/TourIconGallery';
import PhotoIconGallery from 'components/icons/PhotoIconGallery';
import { isValidUrl } from 'utils/validateUrlSrc';
// import LazyLoad from "react-lazyload";

const Container = styled.div`
  min-height: 280px;
  & > div > div:nth-child(2) {
    z-index: 1;
    height: 100%;
  }
`;

const GalleryHeader = styled.div`
  ${({ showNewSchemaColor, newSchemaColorHexaCode }) =>
    showNewSchemaColor && `background-color: ${newSchemaColorHexaCode};`}

  @media ${breakpoints.md} {
    height: 3.75rem;
  }
`;
const FiltersContainer = styled.div`
  height: 2.25rem;
`;

const IconContainer = styled.div`
  width: 2.25rem;
  height: 2.25rem;
`;

const Iframe = styled.iframe`
  height: 280px;
  @media ${breakpoints.md} {
    height: 24.375rem;
  }
  @media ${breakpoints.lg} {
    height: 33.125rem;
  }
  @media ${breakpoints.xl} {
    height: 40.6rem;
  }
`;

const InternalVideo = styled.video``;

const ImgContainer = styled.img`
  height: 280px;
  width: 290px;
  @media ${breakpoints.sm} {
    width: 315px;
  }
  @media ${breakpoints.md} {
    width: auto;
    height: 24.375rem;
  }
  @media ${breakpoints.lg} {
    width: auto;
    height: 33.125rem;
  }
  @media ${breakpoints.xl} {
    width: auto;
    height: 40.6rem;
  }
`;
const IframeContainer = styled.div`
  width: 290px;
  height: 280px;
  @media ${breakpoints.md} {
    width: 990px;
    height: 24.375rem;
  }
  @media ${breakpoints.lg} {
    height: 33.125rem;
  }
  @media ${breakpoints.xl} {
    height: 40.6rem;
  }
`;

const SliderContainer = styled.div`
  height: 280px;
  @media ${breakpoints.md} {
    height: 24.375rem;
  }
  @media ${breakpoints.lg} {
    height: 33.125rem;
  }
  @media ${breakpoints.xl} {
    height: 40.6rem;
  }
`;
const CustomArrowContainer = styled.div`
  &:before {
    display: none;
  }
  z-index: 2;
`;

const FiltersMediaButton = styled.button`
  ${({ isTour }) => isTour && 'width: 7.5rem;'}

  ${({ showNewSchemaColor, newSchemaTextColorHexaCode }) =>
    showNewSchemaColor ? `border-color: ${newSchemaTextColorHexaCode};` : ''}
  ${({ showNewSchemaColor, newSchemaTextColorHexaCode, isFilterSelected }) =>
    showNewSchemaColor &&
    isFilterSelected &&
    `background-color: ${newSchemaTextColorHexaCode};`}
  ${({ showNewSchemaColor, newSchemaColorHexaCode, isFilterSelected }) =>
    showNewSchemaColor &&
    !isFilterSelected &&
    `background-color: ${newSchemaColorHexaCode};`}
`;
const FilterMediaIconContainer = styled.div`
  ${({ showNewSchemaColor, isFilterSelected, newSchemaColorHexaCode }) =>
    showNewSchemaColor &&
    isFilterSelected &&
    `color: ${newSchemaColorHexaCode};`}
  ${({ showNewSchemaColor, isFilterSelected, newSchemaColorHexaCode }) =>
    !showNewSchemaColor && isFilterSelected && 'color: white;'}
`;
const FilterMediaTextContainer = styled.span`
  ${({ showNewSchemaColor, isFilterSelected, newSchemaColorHexaCode }) =>
    showNewSchemaColor &&
    isFilterSelected &&
    `color: ${newSchemaColorHexaCode};`}
  ${({ showNewSchemaColor, isFilterSelected, newSchemaColorHexaCode }) =>
    !showNewSchemaColor && isFilterSelected && 'color: white;'}
`;

const isFilterSelected = (item, filter) =>
  (item.text === 'Videos' && filter === 'video') ||
  (item.text === '360 Tours' && filter === 'tour') ||
  (item.text === 'Photos' && filter === 'image');

const DynamicWidthGallery = ({
  className,
  carousel,
  showFavorites = false,
  showShare = false,
  onChange = () => {},
  handleFilterChange = () => {},
  filter,
  favoritesList,
  saveFavorite,
  deleteFavorite,
  showNewSchemaColor,
  newSchemaColorHexaCode,
  newSchemaTextColorHexaCode,
  filteredCtas,
  position,
  setPosition,
}) => {
  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);
  const { ContextID = {} } = useSitecoreContext() || {};

  const id = ContextID.value;

  const [isSharedVisible, setIsSharedVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [chekBoxFocus, setChekBoxFocus] = useState(0);
  const [chekBoxFocusTitle, setChekBoxFocusTitle] = useState(0);
  const [chekBoxFocusGlobal, setChekBoxFocusGlobal] = useState(0);
  const isIOS = getMobileOS();
  const itemRefs = useRef([]);
  let sliderRef = useRef(null);
  const len = carousel.length;
  const dropdown = useSpring({
    height: open && filteredCtas ? filteredCtas.length * 38 : 0,
    delay: open ? 100 : 0,
    'margin-top':
      open && filteredCtas
        ? filteredCtas.length === 3
          ? isIOS
            ? 80
            : 152
          : filteredCtas.length === 2
          ? isIOS
            ? 60
            : 124
          : 0
        : 0,
  });

  const shareDropdown = useSpring({
    height: isSharedVisible ? (isMobile ? 88 : 46) : 0,
    delay: isSharedVisible ? 100 : 0,
  });

  const handleLoopNext = () => {
    sliderRef.slickGoTo(0);
  };
  const handleLoopPrev = () => {
    sliderRef.slickGoTo(carousel.length - 1);
  };
  const CustomNextArrow = (props) => {
    const { className, style, onClick } = props;
    return (
      <CustomArrowContainer
        className={cx('mr-16', className)}
        style={{ ...style }}
        onClick={position === carousel.length - 1 ? handleLoopNext : onClick}
      >
        <IconButton
          className={cx(
            'absolute hidden md:flex right-0 bottom-0 top-0 m-auto z-20 transition-opacity duration-200 mr-10 w-10 h-10 md:w-16 md:h-16 md:mr-5',
          )}
          icon='arrow-right'
          idIcon={'DynamicWidthGallery'}
          color='white'
          circled
          shadowed
          hoverState
          buttonLabel='Next slide on gallery carousel'
          title={'Right arrow icon'}
          tabIndex={0}
        />
      </CustomArrowContainer>
    );
  };
  const CustomPrevArrow = (props) => {
    const { className, style, onClick } = props;
    return (
      <CustomArrowContainer
        className={cx('ml-16', className)}
        style={{ ...style }}
        onClick={position === 0 ? handleLoopPrev : onClick}
      >
        <IconButton
          className='absolute hidden md:flex left-0 bottom-0 top-0 m-auto z-20 transition-opacity duration-200 ml-10 w-10 h-10 md:w-16 md:h-16 md:ml-5'
          icon='arrow-left'
          idIcon={'DynamicWidthGallery'}
          color='white'
          circled
          shadowed
          hoverState
          buttonLabel='Previous slide on gallery carousel'
          title={'Left arrow icon'}
          tabIndex={0}
        />
      </CustomArrowContainer>
    );
  };

  /* useEffect(() => {
    if (itemRefs.current) {
      setTimeout(() => {
        setPosition(0);
        itemRefs.current.forEach((item, index) => {
          if (item) {
            item.setAttribute("aria-hidden", true);
            if (index === 0) {
              item.tabIndex = 0;
              item.focus();
              item.tabIndex = -1;
            } else {
              item.tabIndex = -1;
              item.setAttribute("aria-hidden", true);
            }
          }
        });
      }, 500);
    }
  }, [carousel]);*/

  useEffect(() => {
    onChange((len - Math.abs(position % len)) % len);
  }, [position, len]);

  const itemTypes = carousel.reduce((types, item) => {
    if (!types.includes(item.type)) {
      types.push(item.type);
    }
    return types;
  }, []);

  const isFavorite = favoritesList.includes(id);

  const handleFavoriteClick = (id) => {
    if (!isFavorite) {
      saveFavorite(id);
    } else {
      deleteFavorite(id);
    }
  };

  const getFilterLabel = () => {
    for (let i = 0; i < filteredCtas.length; i++) {
      if (isFilterSelected(filteredCtas[i], filter)) {
        return filteredCtas[i].text;
      }
    }
    return 'All';
  };

  const sliderSettings = {
    className: 'slider variable-width w-full h-auto md:h-full',
    dots: false,
    infinite: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    variableWidth: carousel.length > 1,
    centerMode: true,
    swipeToSlide: true,
    touchThreshold: 100,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    afterChange: (current) => {
      setPosition(current);
      const itemRef = itemRefs.current[current];
      itemRefs.current.forEach((ref, index) => {
        if (index === current) {
          ref.tabIndex = 0;
        }
      });
      if (itemRef) {
        itemRef.tabIndex = 0;
        itemRef.focus();
      }
    },
  };

  return (
    <>
      <GalleryHeader
        className={cx(
          'relative h-12 md:h-20 w-full md:rounded flex justify-center md:justify-between items-center px-4 mb-4',
          {
            'bg-action-blue': !showNewSchemaColor,
            // hidden: !isMobile && filteredCtas && filteredCtas.length <= 1,
          },
        )}
        showNewSchemaColor={showNewSchemaColor}
        newSchemaColorHexaCode={newSchemaColorHexaCode}
      >
        {!isMobile && filteredCtas && filteredCtas.length > 1 && (
          <FiltersContainer
            onFocus={(e) => setChekBoxFocusGlobal(0)}
            tabIndex={chekBoxFocusGlobal === -1 ? 0 : -1}
            className='absolute top-0 left-0 right-0 rounded-full text-md text-white mt-3 flex justify-center items-center font-trade-gothic-20 pt-1'
          >
            {filteredCtas &&
              filteredCtas.map((item, i) => (
                <>
                  <FiltersMediaButton
                    onFocus={() => setChekBoxFocusGlobal(0)} // second slide}
                    className={cx(
                      'h-full flex flex-row  uppercase border-2 items-center',
                      {
                        'pl-2 rounded-l-full': i === 0,
                        'pr-2 rounded-r-full': i === filteredCtas.length - 1,
                        'border-mattamy-blue ': !showNewSchemaColor,
                        'border-mattamy-blue bg-mattamy-blue':
                          !showNewSchemaColor && isFilterSelected(item, filter),
                        'border-mattamy-blue bg-white':
                          !showNewSchemaColor &&
                          !isFilterSelected(item, filter),
                        'border-white ': showNewSchemaColor,
                      },
                    )}
                    tabIndex={chekBoxFocusGlobal}
                    showNewSchemaColor={showNewSchemaColor}
                    isFilterSelected={isFilterSelected(item, filter)}
                    newSchemaColorHexaCode={newSchemaColorHexaCode}
                    newSchemaTextColorHexaCode={newSchemaTextColorHexaCode}
                    isTour={item.text === '360 Tours '}
                    key={item.text}
                    role='radio'
                    aria-checked={isFilterSelected(item, filter)}
                    onClick={(e) => {
                      handleFilterChange(item.text);
                      setChekBoxFocusGlobal(-1);
                      setChekBoxFocus(-1);
                      setChekBoxFocusTitle(item.text);
                      const itemRef = itemRefs.current[0];
                      if (itemRef) {
                        itemRef.tabIndex = 0;
                      }
                    }}
                  >
                    <FilterMediaIconContainer
                      className={cx('pl-2 flex w-8', {
                        'text-mattamy-blue':
                          !showNewSchemaColor &&
                          !isFilterSelected(item, filter),
                        'text-white':
                          showNewSchemaColor && !isFilterSelected(item, filter),
                        // "text-transparent": isFilterSelected(item, filter),
                      })}
                      showNewSchemaColor={showNewSchemaColor}
                      isFilterSelected={isFilterSelected(item, filter)}
                      newSchemaColorHexaCode={newSchemaColorHexaCode}
                      newSchemaTextColorHexaCode={newSchemaTextColorHexaCode}
                    >
                      {item.text === 'Videos' && (
                        <VideoIconGallery className={'w-8'} />
                      )}
                      {item.text === '360 Tours' && (
                        <TourIconGallery className={'w-8'} />
                      )}
                      {item.text === 'Photos' && (
                        <PhotoIconGallery className={'w-8'} />
                      )}
                    </FilterMediaIconContainer>

                    <FilterMediaTextContainer
                      className={cx('ml-1 mt-1  pr-1', {
                        'text-mattamy-blue':
                          !showNewSchemaColor &&
                          !isFilterSelected(item, filter),
                        'text-white':
                          showNewSchemaColor && !isFilterSelected(item, filter),
                        // "text-black": isFilterSelected(item, filter),
                      })}
                      showNewSchemaColor={showNewSchemaColor}
                      isFilterSelected={isFilterSelected(item, filter)}
                      newSchemaColorHexaCode={newSchemaColorHexaCode}
                      newSchemaTextColorHexaCode={newSchemaTextColorHexaCode}
                    >
                      {item.text}
                    </FilterMediaTextContainer>
                  </FiltersMediaButton>
                </>
              ))}
          </FiltersContainer>
        )}
        {isMobile && filteredCtas && filteredCtas.length > 0 && (
          <>
            <div
              className='inline-block uppercase font-trade-gothic-20 text-white font-semibold cursor-pointer mt-1'
              onClick={() => setOpen(!open)}
            >
              <span className='mr-2'>{getFilterLabel()}</span>
              <ChevronDown
                className={cx(
                  'inline-block w-10-mattamy mb-1 transition-all duration-500 transform',
                  {
                    '-rotate-180': open,
                  },
                )}
              />
            </div>
            <animated.div
              className={cx(
                'absolute z-20 w-full flex flex-col justify-center overflow-auto',
                {
                  'bg-action-blue ': !showNewSchemaColor,
                  'bg-black ': showNewSchemaColor,
                },
              )}
              style={dropdown}
            >
              {filteredCtas &&
                filteredCtas.map((item, i) => (
                  <button
                    className={cx(
                      'uppercase tracking-tight leading-none active:outline-none focus:outline-none font-trade-gothic-20 text-white font-semibold pt-1 my-2',
                    )}
                    key={item.text}
                    onClick={(e) => {
                      handleFilterChange(item.text);
                      setOpen(false);
                      setChekBoxFocus(-1);
                      setChekBoxFocusTitle(item.title);
                    }}
                    tabIndex={chekBoxFocus}
                  >
                    {item.text}
                  </button>
                ))}
            </animated.div>
          </>
        )}
        <p
          aria-live='polite'
          style={{
            position: 'absolute',
            left: '-10000px',
            top: ' auto',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
          }}
        >
          Slide has changed view to {chekBoxFocusTitle}
        </p>

        <div className='absolute right-0 md:top-0 md:right-0 flex md:mt-3 mr-5 justify-center items-center flex flex-col'>
          <div className={cx('flex justify-between items-start pt-0')}>
            {showFavorites && (
              <IconContainer
                className='flex items-center justify-center rounded-full bg-white text-mattamy-blue mr-10-mattamy hover:bg-mattamy-blue hover:text-white cursor-pointer'
                onClick={() => handleFavoriteClick(id)}
                aria-label={
                  isFavorite ? 'Mark as unfavorite' : 'Mark as favorite'
                }
                role='button'
                aria-pressed={isFavorite ? 'true' : 'false'}
                tabIndex={0}
              >
                {isFavorite ? (
                  <HeartFilled className='h-5 w-5' />
                ) : (
                  <HeartIcon className='h-5 w-5' />
                )}
              </IconContainer>
            )}
            {showShare && (
              <div
                onBlur={() => {
                  setTimeout(() => {
                    setIsSharedVisible(false);
                  }, 100);
                }}
              >
                <IconContainer
                  className={cx(
                    'flex items-center justify-center rounded-full bg-white text-mattamy-blue hover:bg-mattamy-blue hover:text-white focus:outline-none cursor-pointer',
                    { 'bg-mattamy-blue text-white': isSharedVisible },
                  )}
                  tabIndex='1'
                  onClick={() => {
                    setIsSharedVisible(!isSharedVisible);
                  }}
                  aria-label={'Share Options'}
                >
                  <ShareIcon
                    className={cx('h-5', {
                      'text-white': isSharedVisible,
                    })}
                  />
                </IconContainer>
                {isSharedVisible && (
                  <animated.div
                    className='absolute z-20 w-full flex flex-col justify-center overflow-hidden'
                    style={shareDropdown}
                  >
                    <div
                      className='st-custom-button z-50'
                      onClick={() => {
                        shareThis({
                          dataNetwork: 'email',
                          dataUrl: getUrl(),
                        });
                      }}
                      aria-label={'Share by email'}
                    >
                      <IconContainer
                        data-aos='fade-down'
                        data-aos-duration='100'
                        className='rounded-full bg-mattamy-blue mt-2 flex items-center justify-center focus:outline-none'
                        tabIndex={0}
                      >
                        <MailIcon className='h-5 w-5 text-white' />
                      </IconContainer>
                    </div>
                    {isMobile && (
                      <a
                        href={`sms:${
                          isIOS === 'iOS' ? '&' : '?'
                        }body=${getUrl()}`}
                        onClick={() => {
                          track({ eventId: trackingEvents.ClickToText });
                          trackAnalyticsEvent({
                            gtagEvent: 'ShareClick',
                            mouseflowTag: 'Share',
                          });
                        }}
                        aria-label={'Share by text message'}
                      >
                        <IconContainer
                          data-aos='fade-down'
                          data-aos-duration='100'
                          data-aos-delay='50'
                          className='rounded-full bg-mattamy-blue mt-2 flex items-center justify-center focus:outline-none'
                          tabIndex={0}
                        >
                          <ChatIcon className='h-5 w-5 text-white' />
                        </IconContainer>
                      </a>
                    )}
                  </animated.div>
                )}
              </div>
            )}
          </div>
        </div>
      </GalleryHeader>
      <AspectRatio ratio={isMobile ? '375/280' : '1200/650'} className='w-full'>
        <Container
          className={cx(
            'w-full h-full md:rounded overflow-hidden relative flex flex-col items-center',
          )}
        >
          <Slider
            ref={(slider) => {
              sliderRef = slider;
            }}
            tabIndex={0}
            {...sliderSettings}
          >
            {carousel &&
              carousel.map((item, index) => {
                const isVideo = ['internal', 'external'].includes(item.type);
                const itemVideoType = getVideoType(item.src);
                const description = item.description;

                return (
                  <SliderContainer
                    className={cx(
                      'w-auto justify-center text-center rounded z-0 ',
                      {
                        'mx-2': carousel.length > 1,
                        'md:ml-12': carousel.length === 1,
                      },
                    )}
                    key={item.image ? item.image.src : item.id}
                  >
                    {item.type === 'image' ? (
                      index === 0 ? (
                        <ImgContainer
                          onFocus={(e) => {
                            itemRefs.current.forEach((ref) => {
                              if (ref && ref !== e.target) {
                                ref.tabIndex = -1;
                              }
                            });
                          }}
                          tabIndex={0}
                          ref={(element) => (itemRefs.current[index] = element)}
                          src={item.src}
                          alt={item.alt}
                          className='object-cover md:object-contain rounded' // "h-full object-scale-down"
                        />
                      ) : (
                        <div>
                          <ImgContainer
                            onFocus={(e) => {
                              itemRefs.current.forEach((ref) => {
                                if (ref && ref !== e.target) {
                                  ref.tabIndex = -1;
                                }
                              });
                            }}
                            tabIndex={0}
                            ref={(element) =>
                              (itemRefs.current[index] = element)
                            }
                            src={item.src}
                            alt={item.alt}
                            className='object-cover md:object-contain rounded' // "h-full object-scale-down"
                          />
                        </div>
                      )
                    ) : (
                      ''
                    )}
                    {isVideo && itemVideoType === 'external' && (
                      <IframeContainer className='videoIframe bg-light-blue w-full rounded'>
                        <Iframe
                          onFocus={(e) => {
                            itemRefs.current.forEach((ref) => {
                              if (ref && ref !== e.target) {
                                ref.tabIndex = -1;
                              }
                            });
                          }}
                          tabIndex={0}
                          ref={(element) => (itemRefs.current[index] = element)}
                          src={getEmbedUrl(item.src, false, true)}
                          className=' w-full rounded '
                          title={description}
                          loading='lazy'
                        />
                      </IframeContainer>
                    )}
                    {isVideo && itemVideoType === 'internal' && (
                      <IframeContainer className=' w-full  '>
                        <InternalVideo
                          onFocus={(e) => {
                            itemRefs.current.forEach((ref) => {
                              if (ref && ref !== e.target) {
                                ref.tabIndex = -1;
                              }
                            });
                          }}
                          tabIndex={0}
                          ref={(element) => (itemRefs.current[index] = element)}
                          className=' w-full rounded bg-light-blue'
                          src={item.src}
                          controls
                          disablepictureinpicture='true'
                          controlslist='nodownload'
                        />
                      </IframeContainer>
                    )}
                    {item.type === 'tour' && (
                      <IframeContainer className=' w-full  '>
                        <Iframe
                          onFocus={(e) => {
                            itemRefs.current.forEach((ref) => {
                              if (ref && ref !== e.target) {
                                ref.tabIndex = -1;
                              }
                            });
                          }}
                          tabIndex={0}
                          ref={(element) => (itemRefs.current[index] = element)}
                          src={getScrFromIframe(item.src)}
                          className=' w-full rounded '
                          title={description}
                          loading='lazy'
                        />
                      </IframeContainer>
                    )}
                    {/* description && (
                      <div
                        className={cx(
                          "flex flex-col text-center -mt-16 md:-mt-32"
                        )}
                      >
                        <p className="text-center text-lg font-graphie font-bold text-white">
                          {description}
                        </p>
                      </div>
                    ) */}
                  </SliderContainer>
                );
              })}
          </Slider>
          {!isMobile && carousel.length > 1 && (
            <div
              className={cx(
                'bg-white absolute bottom-0 mb-12 rounded-full text-sm text-mattamy-blue px-3',
                'flex justify-center items-center font-trade-gothic-20 z-10',
              )}
            >
              <p className='mt-1'>
                {position + 1}/{len}
              </p>
            </div>
          )}
        </Container>
      </AspectRatio>
    </>
  );
};

DynamicWidthGallery.propTypes = {
  carousel: PropTypes.array.isRequired,
  ctas: PropTypes.array.isRequired,
  className: PropTypes.string,
  showFavorites: PropTypes.bool,
  showShare: PropTypes.bool,
  availableDateMessage: PropTypes.string,
  modelHome: PropTypes.string,
  onChange: PropTypes.func,
  handleImageClick: PropTypes.func,
  handleFilterChange: PropTypes.func,
};

export default DynamicWidthGallery;
